export interface LoadingStateHost {
    host: () => HTMLElement;
    editable: () => HTMLElement | null;
    loading: () => HTMLElement | null;
}

/**
 * What the box shows while the editor script downloads: the host's `aria-busy` and a status line,
 * both rendered by the component and removed here, never hidden. It also remembers a press on the
 * editable area, so the caret can go there once the editor is ready.
 */
export class RichTextEditorLoadingState {
    private pressed = false;
    private pressTarget: HTMLElement | null = null;

    private readonly onPointerDown = (): void => {
        this.pressed = true;
    };

    public constructor(private readonly parts: LoadingStateHost) {}

    /** Starts listening for a press on the editable area as it is now, with a clean record. */
    public begin(): void {
        this.reset();
        this.pressTarget = this.parts.editable();
        this.pressTarget?.addEventListener("pointerdown", this.onPointerDown);
    }

    /** Stops listening and forgets a press, as a disconnect does. */
    public reset(): void {
        this.pressTarget?.removeEventListener("pointerdown", this.onPointerDown);
        this.pressTarget = null;
        this.pressed = false;
    }

    /** Whether the person pressed the editable area since `begin()`. */
    public wasPressed(): boolean {
        return this.pressed;
    }

    /** Ends the loading state, for an editor that started and for a download that failed alike. */
    public finish(): void {
        this.parts.loading()?.remove();
        this.parts.host().removeAttribute("aria-busy");
        this.reset();
    }
}
